import { CardPolicyNotice } from '../components/CardPolicyNotice';
import { useState } from 'preact/hooks';
import { logout } from '../api/client';
import { BookingSiteSettings } from './BookingSiteSettings';
import { ActivationPanel } from '../components/ActivationPanel';
import { ReconnectSquare } from '../components/ReconnectSquare';
import { CardPermissions } from '../components/CardPermissions';
import { SquareLocationPicker } from '../components/SquareLocationPicker';
import { ASSISTANT_INITIAL, ASSISTANT_NAME } from '../brand.js';

export function SettingsView({ dashboardData, onTeachJonathan, onBilling }) {
  const { salon_name, square_integration } = dashboardData;
  const [showSquareDetail, setShowSquareDetail] = useState(false);
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const [eligibilityRevision, setEligibilityRevision] = useState(0);

  const ownerInitials = salon_name
    ?.split(' ')
    .map(w => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase() || 'CL';

  const handleSignOut = logout;

  const formatDate = (isoString) => {
    if (!isoString) return 'Unknown';
    const date = new Date(isoString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', day: 'numeric', year: 'numeric', 
      hour: 'numeric', minute: '2-digit' 
    });
  };

  return (
    <div
      class="view active fixed inset-0 overflow-y-auto"
      style={{
        background: 'var(--cream)',
        paddingBottom: 'calc(80px + var(--safe-area-bottom))',
      }}
    >
      <header
        class="settings-header"
        style={{
          background: 'rgba(255,255,255,0.85)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          borderBottom: '1px solid var(--border-subtle)',
          padding: 'calc(1rem + var(--safe-area-top)) 1rem 1rem',
          position: 'sticky',
          top: 0,
          zIndex: 10,
        }}
      >
        <div class="desktop-container">
          <h1 style={{ fontSize: '1.375rem', fontWeight: 700, margin: 0 }}>Settings</h1>
          <p style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', margin: '0.25rem 0 0' }}>
            {salon_name || 'Your Salon'}
          </p>
          {dashboardData?.ai_available === false && <p role="status" style={{ margin: '0.5rem 0 0', fontSize: '0.8125rem' }}>AI assistance is paused. Your salon settings and billing controls remain available.</p>}
        </div>
      </header>

      <div style={{ padding: '1.25rem 1rem' }}>
        <div class="desktop-container">
          <div
            class="settings-grid"
            style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', minHeight: 'calc(100vh - 200px)' }}
          >
            <SettingsSection title="Account">
              <div class="card" style={{ padding: 0, overflow: 'hidden' }}>
                <SettingsRow>
                  <div
                    style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '50%',
                      background: 'var(--sage)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontWeight: 600,
                      fontSize: '0.9375rem',
                    }}
                  >
                    {ownerInitials}
                  </div>
                  <div style={{ flex: 1 }}>
                    <h3 style={{ fontSize: '1rem', fontWeight: 600, margin: 0 }}>
                      {salon_name || 'Salon Owner'}
                    </h3>

                  </div>
                </SettingsRow>
                <SettingsRow noBorder>
                  <span style={{ fontSize: '0.9375rem' }}>Subscription</span>
                  <button type="button" onClick={onBilling} style={{ background: 'none', border: 'none', color: 'var(--sage)', textDecoration: 'underline', minHeight: '44px', cursor: 'pointer' }}>View billing &amp; usage</button>
                </SettingsRow>
              </div>
            </SettingsSection>

            <ActivationPanel onActivated={() => setEligibilityRevision(value => value + 1)} key={`activation-${dashboardData.salon_id || salon_name}`} />

            <BookingSiteSettings eligibilityRevision={eligibilityRevision} key={dashboardData.salon_id || salon_name} salonName={salon_name} />

            <SettingsSection title={ASSISTANT_NAME}>
              <div class="card" style={{ padding: 0, overflow: 'hidden' }}>
                <CardPolicyNotice required={dashboardData?.card_policy_review_required} disabled={dashboardData?.ai_available === false} onReview={onTeachJonathan} />
                <SettingsRow interactive noBorder onClick={onTeachJonathan}>
                  <div
                    class="logo"
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      background: 'var(--sage)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: '1.125rem',
                      flexShrink: 0,
                    }}
                  >
                    {ASSISTANT_INITIAL.toLowerCase()}
                  </div>
                  <div style={{ flex: 1 }}>
                    <h3 style={{ fontSize: '0.9375rem', fontWeight: 500, margin: 0 }}>Talk to {ASSISTANT_NAME}</h3>
                    <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', margin: 0 }}>
                      Teach {ASSISTANT_NAME} more about how your salon runs
                    </p>
                  </div>
                  <ChevronIcon />
                </SettingsRow>
              </div>
            </SettingsSection>

            <SettingsSection title="Integrations">
              <div class="card" style={{ padding: 0, overflow: 'hidden' }}>
                <SettingsRow interactive onClick={() => setShowSquareDetail(true)}>
                  <IntegrationIcon type="square" />
                  <div style={{ flex: 1 }}>
                    <h3 style={{ fontSize: '0.9375rem', fontWeight: 500, margin: 0 }}>Square</h3>
                    <p style={{ fontSize: '0.75rem', color: 'var(--sage)', margin: 0 }}>Connection saved</p>
                  </div>
                  <ChevronIcon />
                </SettingsRow>
                <CardPermissions key={dashboardData.salon_id} salonId={dashboardData.salon_id} />
                <div style={{ padding: '0 1rem 1rem', borderBottom: '1px solid var(--border-subtle)' }}><ReconnectSquare /><button type="button" onClick={() => setShowLocationPicker(!showLocationPicker)} style={{ minHeight: '44px', padding: '0.75rem', marginTop: '0.75rem', font: 'inherit' }}>{showLocationPicker ? 'Close location settings' : 'Review Square location'}</button>{showLocationPicker && <SquareLocationPicker onConfirmed={() => setShowLocationPicker(false)} />}</div>
                <SettingsRow noBorder disabled>
                  <IntegrationIcon type="instagram" disabled />
                  <div style={{ flex: 1 }}>
                    <h3 style={{ fontSize: '0.9375rem', fontWeight: 500, margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      Instagram
                      <ComingSoonBadge />
                    </h3>
                    <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', margin: 0 }}>Not connected</p>
                  </div>
                </SettingsRow>
              </div>
            </SettingsSection>

            {showSquareDetail && (
              <SquareDetailSheet 
                integration={square_integration}
                formatDate={formatDate}
                onClose={() => setShowSquareDetail(false)} 
              />
            )}

            <div style={{ marginTop: 'auto', paddingTop: '2rem', borderTop: '1px solid var(--border-subtle)' }}>
              <SettingsSection title="Support">
                <div class="card" style={{ padding: 0, overflow: 'hidden' }}>
                  <SettingsRow
                    interactive
                    onClick={() => {
                      window.location.href =
                        `mailto:support@gojonathan.ai?subject=Help%20with%20${ASSISTANT_NAME}`;
                    }}
                  >
                    <span style={{ fontSize: '0.9375rem' }}>Help Center</span>
                    <ChevronIcon />
                  </SettingsRow>
                  <SettingsRow
                    interactive
                    onClick={() => {
                      window.location.href =
                        'mailto:support@gojonathan.ai?subject=Support%20request';
                    }}
                  >
                    <span style={{ fontSize: '0.9375rem' }}>Contact Support</span>
                    <ChevronIcon />
                  </SettingsRow>
                  <SettingsRow interactive noBorder onClick={handleSignOut}>
                    <span style={{ fontSize: '0.9375rem', color: 'var(--coral)' }}>Sign Out</span>
                    <svg width="20" height="20" fill="none" stroke="var(--coral)" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                  </SettingsRow>
                </div>
              </SettingsSection>
            </div>
          </div>

          <p style={{ textAlign: 'center', fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '2rem' }}>
            {ASSISTANT_NAME} v1.0.0
          </p>
        </div>
      </div>
    </div>
  );
}

function SettingsSection({ title, children, comingSoon, disabled }) {
  return (
    <div style={disabled ? { opacity: 0.6 } : undefined}>
      <h2
        style={{
          fontSize: '0.6875rem',
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          color: 'var(--text-secondary)',
          margin: '0 0 0.875rem 0.25rem',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        {title}
        {comingSoon && <ComingSoonBadge />}
      </h2>
      {children}
    </div>
  );
}

function SettingsRow({ children, interactive, noBorder, disabled, onClick }) {
  const baseStyle = {
    padding: '1rem',
    borderBottom: noBorder ? 'none' : '1px solid var(--border-subtle)',
    display: 'flex',
    alignItems: 'center',
    gap: '0.875rem',
    justifyContent: 'space-between',
    ...(disabled && { opacity: 0.55, cursor: 'not-allowed' }),
  };

  if (interactive && !disabled) {
    return (
      <div
        style={{ ...baseStyle, cursor: 'pointer', transition: 'background 0.15s ease' }}
        onMouseOver={(e) => { e.currentTarget.style.background = 'var(--cream-light)'; }}
        onMouseOut={(e) => { e.currentTarget.style.background = 'transparent'; }}
        onClick={onClick}
      >
        {children}
      </div>
    );
  }

  return <div style={baseStyle}>{children}</div>;
}

function ComingSoonBadge() {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '0.1875rem 0.5rem',
        fontSize: '0.5625rem',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        color: 'var(--periwinkle)',
        background: 'rgba(141, 159, 241, 0.12)',
        borderRadius: '100px',
        marginLeft: '0.5rem',
      }}
    >
      Coming Soon
    </span>
  );
}

function ChevronIcon() {
  return (
    <svg width="20" height="20" fill="none" stroke="var(--text-secondary)" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
    </svg>
  );
}

function IntegrationIcon({ type, disabled }) {
  if (type === 'square') {
    return (
      <div
        style={{
          width: '40px',
          height: '40px',
          borderRadius: '10px',
          background: 'var(--black)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <span style={{ color: 'white', fontSize: '1.125rem', fontWeight: 700 }}>S</span>
      </div>
    );
  }

  if (type === 'instagram') {
    return (
      <div
        style={{
          width: '40px',
          height: '40px',
          borderRadius: '10px',
          background: 'linear-gradient(135deg, #833ab4 0%, #fd1d1d 50%, #fcb045 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          ...(disabled && { opacity: 0.5, filter: 'grayscale(30%)' }),
        }}
      >
        <span style={{ color: 'white', fontSize: '0.875rem', fontWeight: 600 }}>IG</span>
      </div>
    );
  }

  return null;
}

function SquareDetailSheet({ integration, formatDate, onClose }) {
  return (
    <div 
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.4)',
        zIndex: 100,
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
      }}
      onClick={onClose}
    >
      <div 
        style={{
          background: 'white',
          borderRadius: '24px 24px 0 0',
          width: '100%',
          maxWidth: '500px',
          maxHeight: '85vh',
          overflow: 'auto',
          padding: '1.5rem',
        }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <IntegrationIcon type="square" />
            <div>
              <h2 style={{ fontSize: '1.125rem', fontWeight: 600, margin: 0 }}>Square Integration</h2>
              <p style={{ fontSize: '0.75rem', color: 'var(--sage)', margin: 0 }}>Connection saved</p>
            </div>
          </div>
          <button 
            onClick={onClose}
            style={{ 
              background: 'none', border: 'none', cursor: 'pointer', padding: '0.5rem',
              borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}
          >
            <svg width="24" height="24" fill="none" stroke="var(--text-secondary)" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {integration?.last_sync && (
          <div style={{ 
            background: 'var(--cream)', 
            borderRadius: '12px', 
            padding: '1rem', 
            marginBottom: '1.5rem' 
          }}>
            <p style={{ fontSize: '0.6875rem', fontWeight: 600, textTransform: 'uppercase', color: 'var(--text-secondary)', margin: '0 0 0.25rem' }}>
              Last Synced
            </p>
            <p style={{ fontSize: '0.9375rem', fontWeight: 500, margin: 0 }}>
              {formatDate(integration.last_sync)}
            </p>
          </div>
        )}

        <p style={{ fontSize: '0.875rem', lineHeight: 1.5, color: 'var(--text-secondary)' }}>
          Square permissions are checked before checkout and booking activation.
          A saved connection does not mean booking is enabled. Review Booking Activation
          in Settings for your salon's current status.
        </p>

        <p style={{ 
          fontSize: '0.75rem', 
          color: 'var(--text-secondary)', 
          marginTop: '1.5rem',
          lineHeight: 1.5,
          textAlign: 'center'
        }}>
          {ASSISTANT_NAME} only accesses what's needed to help book appointments. 
          You can disconnect anytime from your Square Dashboard.
        </p>
      </div>
    </div>
  );
}
